// `/classroom/:token` — what the purchasing decision-maker sees when a teacher
// forwards the link.
//
// ADDRESSED TO THEM, NOT TO THE TEACHER. This person did not ask for it, has
// twenty of these a week, and is reading it to answer one question: what is
// being asked of my budget? So the page answers that in its first two lines and
// spends the rest earning them.
//
// It asks for nothing. No form, no payment, no account — the next step is a
// reply to a human, because that is the step the offer actually consists of.
// (`convex/classroom.ts` `forPurchaser` returns the teacher's ask and nothing
// else: no ids, no addresses, no status.)

import { useQuery } from "convex/react";
import { Link, useParams } from "react-router";
import { api } from "@bindery/convex/_generated/api";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";

const CONTACT = "rf@sara.ai";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background min-h-dvh">
      <main className="mx-auto max-w-2xl px-6 pt-20 pb-16 sm:px-8">
        {children}
      </main>
    </div>
  );
}

export function PurchaserPage() {
  const { token } = useParams<{ token: string }>();
  const ask = useQuery(api.classroom.forPurchaser, { token: token ?? "" });

  if (ask === undefined) {
    return (
      <Shell>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }

  if (ask === null) {
    return (
      <Shell>
        <h1 className="font-display text-foreground text-4xl font-black tracking-tight">
          This link is no longer open
        </h1>
        <p className="text-muted-foreground mt-4 font-serif text-[1.05rem] leading-relaxed">
          It may have been retired, or already answered. If a teacher in your
          district sent it, write to{" "}
          <a
            href={`mailto:${CONTACT}`}
            className="text-foreground underline decoration-dotted underline-offset-4"
          >
            {CONTACT}
          </a>{" "}
          and we will pick it back up.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="border-foreground text-foreground hover:border-primary hover:text-primary border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
          >
            Browse the shelf
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header>
        <p className="text-muted-foreground font-serif text-sm tracking-[0.35em] uppercase">
          A request from a teacher in your district
        </p>
        <h1 className="font-display text-foreground mt-3 text-4xl font-black tracking-tight text-balance sm:text-5xl">
          {ask.teacherName} would like their class to read the Odyssey
        </h1>
      </header>

      {/* The ask, stated as a record rather than a pitch — this reader is
          checking a claim before they read an argument. */}
      <dl className="mt-8 grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 border-y border-[--rule] py-6 font-serif text-[1.02rem]">
        <dt className="text-muted-foreground">School</dt>
        <dd className="text-foreground">{ask.school}</dd>
        {ask.role !== "" && (
          <>
            <dt className="text-muted-foreground">Teaching</dt>
            <dd className="text-foreground">{ask.role}</dd>
          </>
        )}
        {ask.students !== null && (
          <>
            <dt className="text-muted-foreground">Students</dt>
            <dd className="font-rockwell text-foreground">{ask.students}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Cost to you today</dt>
        <dd className="text-foreground">Nothing. This page sells nothing.</dd>
      </dl>

      {ask.message !== null && (
        <blockquote className="border-foreground text-foreground mt-8 border-l-2 pl-6 font-serif text-[1.05rem] leading-relaxed italic">
          {ask.message}
        </blockquote>
      )}

      <article className="text-foreground mt-10 space-y-5 font-serif text-[1.05rem] leading-relaxed">
        <p className="first-letter:font-display first-letter:float-left first-letter:mt-1 first-letter:pr-3 first-letter:text-[3.4rem] first-letter:leading-[0.8] first-letter:font-black">
          Retells publishes a complete modern-English translation of the Odyssey
          — all twenty-four books, out of Homer’s Greek, with the Greek shown
          beside it and any word tappable for a gloss. It is the register that
          differs from Fitzgerald or Wilson, not the content: nothing
          load-bearing is cut.
        </p>
        <p>
          Every Odyssey a US high school normally assigns is in copyright, so
          the usual choice is to buy a modern translation for each student or to
          assign a public-domain one written before 1900. This is a third
          option, and the reason your teacher wrote to you is that we would
          rather it not cost their students anything.
        </p>
      </article>

      <aside className="border-foreground relative mt-10 border-2 px-7 py-8">
        <p className="bg-background text-muted-foreground absolute -top-[0.6rem] left-6 max-w-[calc(100%-3rem)] px-3 font-serif text-[0.6rem] tracking-[0.2em] whitespace-nowrap uppercase sm:text-[0.7rem] sm:tracking-[0.35em]">
          What we are proposing
        </p>
        <ul className="text-foreground space-y-4 font-serif text-[1.05rem] leading-relaxed">
          <li>
            <span className="font-medium">Students pay nothing.</span> Not per
            seat, not per year. That is the point of the arrangement, not an
            introductory rate.
          </li>
          <li>
            <span className="font-medium">
              We will meet a price you are comfortable with.
            </span>{" "}
            Tell us what your budget looks like and we will work to it rather
            than the other way round.
          </li>
          <li>
            <span className="font-medium">
              If there is no budget, we will find or provide grant funding.
            </span>{" "}
            A district that cannot pay is not a district we turn away.
          </li>
        </ul>
      </aside>

      <section className="mt-10">
        <h2 className="text-muted-foreground font-serif text-[0.75rem] tracking-[0.35em] uppercase">
          The next step
        </h2>
        <p className="text-foreground mt-4 font-serif text-[1.05rem] leading-relaxed">
          Reply to this and we will talk it through — there is no form and no
          procurement portal at this stage.
        </p>
        <a
          href={`mailto:${CONTACT}?subject=${encodeURIComponent(
            `Retells for ${ask.school}`,
          )}`}
          className="border-foreground text-foreground hover:border-primary hover:text-primary mt-5 inline-block border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
        >
          Write to {CONTACT}
        </a>
        <p className="text-muted-foreground mt-6 font-serif text-[0.95rem] leading-relaxed">
          Or read it first — Books 1–5, 9, 13 and 21 are open to anyone, with no
          account:{" "}
          <Link
            to="/read/odyssey/9"
            className="text-foreground underline decoration-dotted underline-offset-4"
          >
            Book 9, the cyclops
          </Link>
          .
        </p>
      </section>
    </Shell>
  );
}
